import React from "react";

import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { fade, makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import FlagIcon from "@material-ui/icons/Flag";

import { ACTIVITY_TYPE_LABELS, isActivityOverdue } from "./crmUtils";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  title: {
    fontWeight: 600,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  overdueCount: {
    borderColor: fade(theme.palette.error.main, 0.45),
    color: theme.palette.error.main,
  },
  row: {
    "&:last-child td": {
      borderBottom: 0,
    },
  },
  overdueRow: {
    backgroundColor: fade(theme.palette.error.main, 0.025),
  },
  activityTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
  },
  subtleFlag: {
    borderColor: fade(theme.palette.primary.main, 0.4),
    color: theme.palette.primary.main,
  },
  overdueText: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  empty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 120,
    padding: theme.spacing(2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
}));

const DealActivitiesPanel = ({
  activities,
  overdueCount,
  saving,
  formatDateTime,
  renderStatus,
  renderActions,
  onCreate,
}) => {
  const classes = useStyles();
  const items = activities || [];

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          Atividades
        </Typography>
        <Box className={classes.headerActions}>
          {overdueCount > 0 ? (
            <Chip
              size="small"
              variant="outlined"
              icon={<ErrorOutlineIcon />}
              label={`${overdueCount} atrasada${overdueCount === 1 ? "" : "s"}`}
              className={classes.overdueCount}
            />
          ) : null}
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={onCreate}
            disabled={saving}
          >
            Nova atividade
          </Button>
        </Box>
      </Box>

      {items.length > 0 ? (
        <TableContainer>
          <Table size="small" aria-label="Atividades do negocio">
            <TableHead>
              <TableRow>
                <TableCell>Atividade</TableCell>
                <TableCell>Prazo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((activity) => {
                const overdue = isActivityOverdue(activity);

                return (
                  <TableRow
                    key={activity.id}
                    className={`${classes.row} ${
                      overdue ? classes.overdueRow : ""
                    }`}
                  >
                    <TableCell>
                      <Box className={classes.activityTitle}>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {activity.title}
                        </Typography>
                        {activity.isNextAction ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={<FlagIcon />}
                            label="Proxima"
                            className={classes.subtleFlag}
                          />
                        ) : null}
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {ACTIVITY_TYPE_LABELS[activity.type] || activity.type}
                        {activity.user?.name ? ` · ${activity.user.name}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        className={overdue ? classes.overdueText : undefined}
                      >
                        {formatDateTime(activity.dueAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderStatus(activity.status)}</TableCell>
                    <TableCell align="right">
                      {renderActions(activity)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box className={classes.empty}>
          <Typography variant="body2">
            Nenhuma atividade cadastrada para este negocio.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DealActivitiesPanel;
